import { useState } from 'react';
import { Link } from 'react-router-dom';
import hospitalRankingsData from '@data/rankings/hospital-rankings.json';

const HospitalRankings = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('comprehensive');

  const categories = hospitalRankingsData.categories;

  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);

  return (
    <div className="container-custom">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          China Hospital Rankings
        </h1>
        <p className="text-lg text-gray-600 mb-6">
          Discover top-tier medical institutions in China based on comprehensive rankings
        </p>
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-primary-800">
            <strong>Source:</strong> {hospitalRankingsData.source} • <strong>Last Updated:</strong> {hospitalRankingsData.lastUpdated}
          </p>
        </div>
      </div>

      <section className="mb-12">
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === category.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {selectedCategoryData && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-2">{selectedCategoryData.name}</h2>
            <p className="text-gray-600">{selectedCategoryData.description}</p>
          </div>
        )}
      </section>

      {selectedCategoryData && (
        <section className="mb-12">
          <div className="space-y-6">
            {selectedCategoryData.rankings.map((hospital) => (
              <div
                key={hospital.rank}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                        hospital.rank === 1 ? 'bg-yellow-500' :
                        hospital.rank === 2 ? 'bg-gray-400' :
                        hospital.rank === 3 ? 'bg-amber-600' :
                        'bg-gray-300'
                      }`}>
                        {hospital.rank}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          {hospital.hospitalName}
                        </h3>
                        <p className="text-sm text-gray-600">{hospital.hospitalNameCn}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {hospital.labels.map((label, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full"
                        >
                          {label}
                        </span>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <span className="text-gray-600">City:</span>{' '}
                        <span className="font-medium">{hospital.city}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Score:</span>{' '}
                        <span className="font-medium text-primary-600">{hospital.score}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Experts:</span>{' '}
                        <span className="font-medium">{hospital.expertCount}+</span>
                      </div>
                      <div>
                        <span className="text-gray-600">International:</span>{' '}
                        <span className={`font-medium ${hospital.internationalDepartment ? 'text-green-600' : 'text-red-600'}`}>
                          {hospital.internationalDepartment ? '✓ Yes' : '✗ No'}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {hospital.highlights.map((highlight, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-md"
                        >
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 md:w-48">
                    {(hospital as any).website && (
                      <a
                        href={(hospital as any).website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors rounded-lg text-sm font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2zm0 14H6m8 0V6" />
                        </svg>
                        Official Website
                      </a>
                    )}
                    {hospital.internationalDepartment && (
                      <Link
                        to={`/hospitals/${hospital.hospitalId}`}
                        className="btn-primary text-center"
                      >
                        View Details
                      </Link>
                    )}
                    <Link
                      to="/services"
                      className="btn-secondary text-center"
                    >
                      Book Consultation
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-gray-50 rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">About These Rankings</h2>
        <div className="space-y-3 text-gray-700">
          <p>
            <strong>Source:</strong> Rankings are based on {hospitalRankingsData.source} annual assessments, the most authoritative hospital evaluation in China.
          </p>
          <p>
            <strong>Criteria:</strong> Hospitals are evaluated based on clinical quality, research capabilities, teaching excellence, and academic reputation.
          </p>
          <p>
            <strong>Last Updated:</strong> {hospitalRankingsData.lastUpdated}. Rankings are typically updated annually.
          </p>
          <p>
            <strong>Note:</strong> These rankings provide a general reference. For medical decisions, always consult with healthcare professionals and consider your specific needs and condition.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HospitalRankings;
